import ConnectWalletStep from "@/components/mint/ConnectWalletStep";
import VerifyAccountsStep from "@/components/mint/VerifyAccountsStep";
import SelectValidatorStep from "@/components/mint/SelectValidatorStep";
import ClaimerMintResult from "@/components/mint/ClaimerMintResult";
import { Button } from "@mantine/core";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import { useCallback, useEffect, useState, FunctionComponent } from "react";
import lottie from "lottie-web";
import giftBoxData from "@/lottie/giftBox.json";
import useMint from "@/hooks/useMintClaimer";
import { trimAddr1 } from "@/utils/format";
import { toast } from "react-toastify";
import { useTypedSelector } from "@/stores";
import FlipClockCountdown from "@leenguyen/react-flip-clock-countdown";
import "@leenguyen/react-flip-clock-countdown/dist/index.css";
import CryptocurrencyPaymentDialog from "@/components/payment/CryptocurrencyPaymentDialog";
import { useDisclosure } from "@mantine/hooks";
import { ClaimerPaymentType, cryptoPayments } from "@/services/payment";
import { CryptocurrencyPayment } from "@/types/payment";
import { zksyncChainId, isMaintenance } from "@/config/config";

let payments = [
  // cryptoPayments.zkSyncKAT,
  cryptoPayments.polygon,
  cryptoPayments.bsc,
  cryptoPayments.ethereum,
  cryptoPayments.zkSync,
];

if (process.env.NEXT_PUBLIC_APP_ENV === "development") {
  payments = [
    cryptoPayments.polygon,
    cryptoPayments.bsc,
    cryptoPayments.ethereum,
    cryptoPayments.zkSync,
    cryptoPayments.zkSyncTestnet,
    cryptoPayments.goerli,
  ];
}

const ZksyncClaimer: FunctionComponent = () => {
  const [claimed, setClaimed] = useState(false);
  const [giftBoxShow, setGiftBoxShow] = useState(false);
  const { verified, form, result } = useVerifyClaim();
  const {
    contractMintWithPay,
    contractMint,
    claimerContractInfo,
    karatMint,
    minting,
    nftMeta,
    isValidLieutenant,
    hasParamLieutenant,
  } = useMint();
  const [mintDialogOpened, { open: openMintDialog, close: closeMintDialog }] =
    useDisclosure(false);

  const claimerContract = useTypedSelector((state) => {
    return state.devtool.config.claimerContract;
  });

  useEffect(() => {
    if (claimerContractInfo.minted && !minting) {
      setClaimed(true);
    }
  }, [claimerContractInfo.minted, minting]);

  const onMintSuccess = useCallback(() => {
    setGiftBoxShow(true);
    const animation = lottie.loadAnimation({
      container: document.getElementById("gift-box") as any,
      renderer: "svg",
      loop: false,
      autoplay: true,
      animationData: giftBoxData,
    });
    animation.addEventListener("complete", () => {
      setGiftBoxShow(false);
      animation.destroy();
      setClaimed(true);
    });
  }, []);

  const onKaratMint = useCallback(async () => {
    try {
      const res = await karatMint();
      if (!res) {
        return;
      }
      onMintSuccess();
    } catch (e: any) {
      toast.error(e.message);
    }
  }, [karatMint]);

  const onContractMint = useCallback(
    async (type: ClaimerPaymentType, amount: number) => {
      try {
        const res = await contractMint();
        if (!res) {
          return;
        }
        onMintSuccess();
      } catch (e: any) {
        toast.error(e.message);
      }
    },
    [contractMint]
  );

  const launchDate = new Date(
    process.env.NEXT_PUBLIC_LAUNCH_CLAIMER_DATE as string
  );
  const less24Hours = launchDate.getTime() - Date.now() < 24 * 1000 * 60 * 60;

  const reachLaunchDate = new Date() >= launchDate;

  const canMint =
    !claimerContractInfo.minted &&
    (process.env.NEXT_PUBLIC_APP_ENV !== "production" || reachLaunchDate) &&
    (!hasParamLieutenant || isValidLieutenant);

  if (claimed) {
    return (
      <ClaimerMintResult
        nftMeta={nftMeta}
        chainId={zksyncChainId}
      ></ClaimerMintResult>
    );
  }

  return (
    <div className="relative">
      <div
        className={`absolute w-full  h-full top-0 left-0 flex items-center justify-center ${
          giftBoxShow ? "z-[999] bg-[rgba(0,0,0,0.6)]" : "-z-10"
        }`}
      >
        <div id="gift-box" className="relative w-[500px] h-[500px]"></div>
      </div>

      <div className="flex-1">
        <div className="bg-[#f9f9f9] py-4 px-8 rounded-xl mb-7 flex gap-10 flex-wrap text-dark2">
          <div className="text-sm">
            <div className="font-semibold mb-1">Quantity</div>
            <div>{claimerContractInfo.quantity.toString()} Minted</div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1">Price</div>
            <div>Free</div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1 flex items-center">
              Contract address{" "}
              <img
                src="/assets/mint/secure3.png"
                className="h-[22px] ml-1"
              ></img>
            </div>
            {claimerContract ? (
              <a
                className="text-primary action hover:text-primary"
                href={`https://explorer.zksync.io/address/${claimerContract}`}
                target="_blank"
                rel="noreferrer"
              >
                {trimAddr1(claimerContract as string, 8)}
              </a>
            ) : (
              <span>Coming soon</span>
            )}
          </div>
        </div>
        <div className="gap-4 flex flex-col">
          <ConnectWalletStep chainId={zksyncChainId}></ConnectWalletStep>
          <VerifyAccountsStep chainId={zksyncChainId}></VerifyAccountsStep>
          <SelectValidatorStep chainId={zksyncChainId}></SelectValidatorStep>
        </div>
        <div className="flex flex-col items-center justify-center mt-10">
          {hasParamLieutenant && !isValidLieutenant && (
            <div className="mb-10 text-error text-sm max-w-[468px] text-center">
              The referral link has an incorrect Lieutenant address. Please
              contact the referrer or remove the address from the URL.
            </div>
          )}
          {!reachLaunchDate && (
            <div className="mb-4">
              <FlipClockCountdown
                renderMap={[!less24Hours, true, true, true]}
                to={launchDate.getTime()}
                digitBlockStyle={{
                  background: "#ffffff",
                  color: "#2d2d2d",
                  height: "24px",
                  width: "22px",
                  fontSize: "16px",
                  fontWeight: 900,
                }}
                labelStyle={{
                  fontSize: 12,
                  color: "dark",
                }}
                separatorStyle={{
                  size: 0,
                }}
                dividerStyle={{
                  height: "0px",
                }}
              />
            </div>
          )}
          {isMaintenance ? (
            <Button
              classNames={{
                inner: "text-body w-[130px]",
                root: "w-[185px]",
              }}
              size="md"
              radius="xl"
              disabled
            >
              Under maintenance
            </Button>
          ) : (
            <Button
              classNames={{
                inner: "text-body w-[100px]",
                root: "w-[155px]",
              }}
              size="md"
              radius="xl"
              disabled={!verified || !claimerContract || !canMint}
              onClick={openMintDialog}
            >
              Mint
            </Button>
          )}
        </div>
      </div>
      <CryptocurrencyPaymentDialog
        title="Mint Claimer"
        serviceId="mint/claimer"
        opened={mintDialogOpened}
        onClose={closeMintDialog}
        payments={payments}
        onMint={async (payment: CryptocurrencyPayment) => {
          if (!payment.enableOneClick) {
            if (
              payment.mintFee?.token?.address ===
              process.env.NEXT_PUBLIC_KAT_CONTRACT
            ) {
              return await onContractMint(
                ClaimerPaymentType.KAT,
                payment.mintFee?.tokenAmount || 0
              );
            } else {
              return await onContractMint(
                ClaimerPaymentType.ETH,
                payment.mintFee?.tokenAmount || 0
              );
            }
          } else {
            return await onKaratMint();
          }
        }}
      ></CryptocurrencyPaymentDialog>
    </div>
  );
};

export default ZksyncClaimer;
